import React, {useEffect, useRef, useState} from "react";
import EventTemplateCategory from "../../../stores/domainStore/pages/models/eventTemplateCategory";
import {ColumnDescription} from "react-bootstrap-table-next";
import {SortOrder} from "../../../shared/enums/sortOrder";
import classNames from "classnames";
import EventTemplateTitle from "../../molecules/eventTemplateTitle/eventTemplateTitle";
import EventTemplateSummaryItem from "../../../stores/domainStore/pages/models/eventTemplateSummaryItem";
import EventCategoryTitle from "../../molecules/eventCategoryTitle/eventCategoryTitle";
import PaginatedDataTable from "../../molecules/paginatedDataTable/paginatedDataTable";
import {useTranslation} from "react-i18next";
import {observer} from "mobx-react-lite";
import "./eventTemplateCategoryItem.sass";
import {useStores} from "../../../stores/useStores";
import OptionMenu from "../../atoms/optionMenu/optionMenu";
import {OptionMenuKeyValue} from "../../../stores/observables/optionMenuKeyValue";
import {ObservableList} from "../../../stores/observables/observableList";
import {format} from "date-fns";
import {EventTemplateStatus} from "../../../shared/enums/eventTemplateStatus";
import Tag from "../../atoms/tag/tag";
import Icon from "../../atoms/common/icon/icon";
import {IconSource} from "../../../shared/enums/iconSource";

export interface IEventTemplateCategoryProps {
  eventTemplateCategory: EventTemplateCategory;
  templateViewStatus: EventTemplateStatus;
  setIsExpanded: (id: string, isExpanded: boolean) => void;
  expandedCategoryId: string;
  sortEventTemplates: (
    categoryId: string,
    sortField: string,
    sortOrder: string
  ) => void;
  onEditTemplate: (eventTemplate: EventTemplateSummaryItem) => void;
  onSelectTemplate: (eventTemplate: EventTemplateSummaryItem) => void;
  onUpdateTemplateStatusClick: (
    eventTemplate: EventTemplateSummaryItem,
    status: EventTemplateStatus
  ) => void;
}

const EventTemplateCategoryItem: React.FC<IEventTemplateCategoryProps> = ({
  eventTemplateCategory: {name, eventTemplatesCount, eventTemplates, id},
  templateViewStatus,
  setIsExpanded,
  expandedCategoryId,
  sortEventTemplates,
  onEditTemplate,
  onSelectTemplate,
  onUpdateTemplateStatusClick
}) => {
  const {t} = useTranslation(["translation", "event", "eventTemplate"]);
  const [scrollHeight, setScrollHeight] = useState<number>(0);
  const {
    domainStore: {eventCatalogueDomainStore},
    uiStore: {eventCatalogueUiStore}
  } = useStores();
  const isExpanded = expandedCategoryId === id;
  const currentSortField = "";
  const currentSortOrder = "";
  const [sortField, setSortField] = useState(currentSortField);
  const [sortOrder, setSortOrder] = useState(currentSortOrder);
  const headerLabelFormatter = (column: ColumnDescription<any, any>) => {
    const isColumnSorting = sortField === column.dataField;
    const isSortAscending = sortOrder === SortOrder.Ascending;
    return (
      <div
        className={classNames("event-catalogue-sortable-header", {
          "is-sort": isColumnSorting
        })}
      >
        {column.text}
        {column.sort && (
          <div>
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
            {!isColumnSorting && <Icon source={IconSource.toggleBoth} />}
          </div>
        )}
      </div>
    );
  };

  const iconHeaderLabelFormatter = () => {
    return (
      <div className="favourite-icon">
        <Icon source={IconSource.favourite} />
      </div>
    );
  };

  const isEnhancedCatalogue = eventCatalogueUiStore.isEnhancedCatalogue;

  const loadImageFn = async (
    template: EventTemplateSummaryItem,
    abortController?: AbortController
  ) => {
    if (!template.image && isExpanded) {
      return await eventCatalogueDomainStore.getEventTemplateImage(
        template.imageId,
        undefined, // we don't have a set width
        undefined, // or a set height
        undefined,
        abortController
      );
    }
    return "";
  };

  const titleFormatter = (
    _: any,
    template: EventTemplateSummaryItem,
    __: any,
    isExpanded: any
  ) => {
    return (
      <EventTemplateTitle
        title={template.title}
        image={template.image}
        shouldLoadImage={isExpanded && isEnhancedCatalogue}
        loadImageFn={(abortController) =>
          loadImageFn(template, abortController)
        }
        onClick={() => onSelectTemplate(template)}
      />
    );
  };

  const descriptionFormatter = (
    _: any,
    {description}: EventTemplateSummaryItem
  ) => {
    const maxLength = 160;
    return description.length > maxLength
      ? description.substring(0, maxLength) + "..."
      : description;
  };

  const lastEditedDateFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    return format(eventTemplate.lastEditedDate, "dd/MM/yy");
  };

  const eventTemplateStatusFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    switch (eventTemplate.status) {
      case EventTemplateStatus.Active:
        return (
          <Tag type="success" text={t("eventTemplate:labels.published")} />
        );
      case EventTemplateStatus.Archived:
        return <Tag type="neutral" text={t("eventTemplate:labels.archived")} />;
    }
    return <></>;
  };

  const favouriteFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    return eventTemplate.isVillageFavourite ? (
      <Icon source={IconSource.favourite}></Icon>
    ) : null;
  };

  /**
   * A list of actions that can be applied to the selected event template.
   */
  var options: OptionMenuKeyValue[] = [];
  switch (templateViewStatus) {
    case EventTemplateStatus.Active:
      options.push(
        new OptionMenuKeyValue("edit", t("translation:actions.edit"), false)
      );
      options.push(
        new OptionMenuKeyValue(
          "archive",
          t("translation:actions.archive"),
          false
        )
      );
      break;
    case EventTemplateStatus.Archived:
      options.push(
        new OptionMenuKeyValue(
          "active",
          t("translation:actions.unarchive"),
          false
        )
      );
      break;
  }
  const actions = new ObservableList<OptionMenuKeyValue>(options);

  const optionMenuFormatter = (
    _: any,
    eventTemplate: EventTemplateSummaryItem
  ) => {
    return (
      <OptionMenu
        options={actions}
        onOptionSelected={(selectedObservableItem: OptionMenuKeyValue) => {
          switch (selectedObservableItem.value) {
            case "edit":
              onEditTemplate(eventTemplate);
              break;
            case "archive":
              onUpdateTemplateStatusClick(
                eventTemplate,
                EventTemplateStatus.Archived
              );
              break;
            case "active":
              onUpdateTemplateStatusClick(
                eventTemplate,
                EventTemplateStatus.Active
              );
              break;
            default:
              // don't know what was clicked, do nothing
              break;
          }
        }}
        disabled={false}
      />
    );
  };

  const columns: ColumnDescription<any, any>[] = [
    {
      dataField: "id",
      hidden: true,
      text: "Id"
    },
    ...(isEnhancedCatalogue
      ? [
          {
            dataField: "favourite",
            text: "",
            headerClasses: "",
            classes: "favourite-col",
            sort: false,
            headerFormatter: iconHeaderLabelFormatter,
            formatter: favouriteFormatter
          }
        ]
      : []),
    {
      dataField: "title",
      text: t("event:labels.title"),
      headerClasses: "",
      classes: "title-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: titleFormatter,
      formatExtraData: isExpanded
    },
    ...(isEnhancedCatalogue
      ? [
          {
            dataField: "description",
            text: t("event:labels.description"),
            headerClasses: "",
            classes: "description-col",
            sort: false,
            headerFormatter: headerLabelFormatter,
            formatter: descriptionFormatter
          }
        ]
      : []),
    {
      dataField: "lastEditedDate",
      text: t("event:labels.lastEditedDate"),
      headerClasses: "",
      classes: "last-edited-date-col",
      sort: true,
      headerFormatter: headerLabelFormatter,
      formatter: lastEditedDateFormatter
    },
    {
      dataField: "status",
      text: t("event:labels.eventTemplateStatus"),
      headerClasses: "",
      classes: "status-col",
      sort: false,
      headerFormatter: headerLabelFormatter,
      formatter: eventTemplateStatusFormatter
    },
    {
      dataField: "optionMenu",
      text: "",
      sort: false,
      classes: "option-menu-col",
      formatter: optionMenuFormatter
    }
  ];

  const sortHandler = (sortField: string, sortOrder: SortOrder): void => {
    setSortField(sortField);
    setSortOrder(sortOrder);
    sortEventTemplates(id, sortField, sortOrder);
  };

  const contentEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentEl.current) {
      setScrollHeight(contentEl.current.scrollHeight);
    }
  }, [isExpanded]);

  return (
    <div
      className="event-category-item card"
      data-testid="eventTemplateCategoryItem"
    >
      <div className="header" id={"heading-" + id}>
        <h2 className="mb-0">
          <button
            className="btn  collapsed"
            type="button"
            data-toggle="collapse"
            data-target={"#collapse-" + id}
            aria-expanded={isExpanded}
            aria-controls={"collapse-" + id}
            onClick={() => setIsExpanded(id, !isExpanded)}
          >
            <EventCategoryTitle
              title={name}
              total={eventTemplatesCount}
              isExpanded={isExpanded}
            />
            {isExpanded ? (
              <Icon source={IconSource.angleUp} />
            ) : (
              <Icon source={IconSource.dropdown} />
            )}
          </button>
        </h2>
      </div>

      <div
        ref={contentEl}
        id={"collapse-" + id}
        className={classNames("expandable")}
        style={
          isExpanded
            ? {
                height: scrollHeight
              }
            : {height: "0px"}
        }
        aria-labelledby={"heading-" + id}
        data-parent="#eventTemplateCategoriesList"
      >
        <PaginatedDataTable
          data={eventTemplates}
          keyField="id"
          columns={columns}
          sizePerPage={eventTemplatesCount}
          dataLength={eventTemplatesCount}
          onSort={sortHandler}
          remote={true}
          noBorderBottom={true}
        />
      </div>
    </div>
  );
};
export default observer(EventTemplateCategoryItem);
